import { useAuth0 } from "@auth0/auth0-react";
import { ItemBar, ItemBarLeft, ItemBarRight } from "components/ItemBar";
import { LoadingSpinner } from "components/LoadingSpinner";
import {
  NOT_SCHEDULED_DATE,
  orderStatusConstants,
  orderStatusMapping,
  orderTypeConstants,
  orderTypeMapping,
  orderTypeShortMapping,
} from "constants/order";
import { events } from "constants/tagManager";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { useAnalytics } from "hooks/useAnalytics";
import usePrice from "hooks/usePrice";
import { useQuery } from "hooks/useQuery";
import { sizer } from "layout/styles/styled/sizer";
import { debounce } from "lodash";
import { Steps } from "pages/OrderOverview/utils/constants";
import { Button } from "primereact/button";
import { Calendar, CalendarValueType } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTableSortOrderType, DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoginWithOrderIdAndDOBMutation } from "store/queries/auth";
import { useGetOrdersQuery } from "store/queries/order";
import { onPublicTokenChange } from "store/slices/auth";
import { onOrderOverviewStepChange } from "store/slices/order";
import styled from "styled-components";
import { IOrderHistory } from "types/Order/Order";

import { AthenaImportDialog } from "../AthenaImportDialog/AthenaImportDialog";
import { HSTImportDialog } from "../HSTImportDialog/HSTImportDialog";
import { CopyOrderDialog } from "./components/CopyOrderDIalog";

dayjs.extend(utc);

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
`;

const StyledIconButton = styled(Button)`
  .p-button-icon {
    font-size: 1.2rem;
  }
`;

// CLE-2677 — the search field, leading the filter row as before but restyled to the Patient
// Estimates design: rounded, with a magnifier inside. Wide enough for the placeholder to fit without
// clipping; no explicit height, so it lines up with the dropdowns beside it.
const StyledSearchBar = styled.span`
  display: inline-block;
  position: relative;
  width: 30rem;
  max-width: 100%;

  /* Doubled to outrank the .p-input-icon-left rules in clear-theme-overrides.css, which have the
     same specificity — a tie there would be decided by stylesheet order. */
  && > i.pi-search {
    left: ${sizer(4)};
    color: var(--gray-new-500);
    font-size: 1.1rem;
  }

  && > .p-inputtext {
    width: 100%;
    padding-left: ${sizer(11)};
    border-radius: ${sizer(3)};
  }
`;
// CLE-2520 — the "changed balance due to autorun" filter. Sits on its own row below the
// filter bar rather than wrapping inline next to Status, so its position never shifts.
const StyledRerunFilter = styled.div`
  display: flex;
  gap: ${sizer(1)};
  align-items: center;
  white-space: nowrap;
  margin-bottom: ${sizer(2)};

  label {
    cursor: pointer;
  }
`;

// CLE-2520 — the old/new Clear Prices of an order's latest automatic eligibility re-run, taken from
// its most recent EligibilityRerun order-history row (`benefitValueChanges.prices.amountInCents`).
// Shown whenever the order has any re-run, even one that left the price unchanged; both null only
// when the order was never re-run.
const getLatestRerunPrices = (
  orderHistories: IOrderHistory[] | undefined
): { old: number | null; new: number | null } => {
  const latestRerun = (orderHistories ?? [])
    .filter((history) => history.type === "EligibilityRerun")
    .reduce<IOrderHistory | null>(
      (latest, history) =>
        !latest ||
        new Date(history.createdAt).getTime() >=
          new Date(latest.createdAt).getTime()
          ? history
          : latest,
      null
    );
  const amount = latestRerun?.benefitValueChanges?.prices?.amountInCents;
  if (!amount) {
    return { old: null, new: null };
  }
  return { old: amount.previousValue, new: amount.newValue };
};

// CLE-2520 — the pre-re-run Clear Price, shown quietly under the new one.
const StyledOldClearPrice = styled.div`
  font-size: 0.75rem;
  color: var(--gray-new-500);
`;

// CLE-2520 — the order's latest automatic eligibility re-run: the new Clear Price on top (normal
// size), the old one beneath it (smaller, gray). Blank only for orders that were never re-run.
const latestRerunPriceBody = (row: {
  latestRerunOldClearPrice: number | null;
  latestRerunNewClearPrice: number | null;
}) =>
  row.latestRerunOldClearPrice === null ||
  row.latestRerunNewClearPrice === null ? (
    ""
  ) : (
    <>
      {parsePrice(centsToDollars(row.latestRerunNewClearPrice))}
      <StyledOldClearPrice>
        {parsePrice(centsToDollars(row.latestRerunOldClearPrice))}
      </StyledOldClearPrice>
    </>
  );

const StyledPageDataTable = styled(DataTable)`
  .p-sortable-column .p-sortable-column-icon {
    display: none !important;
  }
  .p-datatable-tbody > tr > td.p-highlight {
    background-color: unset;
    color: unset;
  }
  .p-sortable-column .p-column-title {
    display: flex;
    gap: ${sizer(1)};
    align-items: center;
    justify-content: space-between;
  }
  .p-datatable-tbody > tr > td {
    padding: 16px 10px !important;
  }
`;

const STATUS_OPTIONS = [
  {
    value: orderStatusConstants.CANCELED,
    name: orderStatusMapping[orderStatusConstants.CANCELED],
  },
  {
    value: orderStatusConstants.DRAFT,
    name: orderStatusMapping[orderStatusConstants.DRAFT],
  },
  {
    value: orderStatusConstants.EXPIRED,
    name: orderStatusMapping[orderStatusConstants.EXPIRED],
  },
  {
    value: orderStatusConstants.PAID,
    name: orderStatusMapping[orderStatusConstants.PAID],
  },
  {
    value: orderStatusConstants.REFUNDED,
    name: orderStatusMapping[orderStatusConstants.REFUNDED],
  },
  {
    value: orderStatusConstants.SENT_TO_PATIENT,
    name: orderStatusMapping[orderStatusConstants.SENT_TO_PATIENT],
  },
  {
    value: orderStatusConstants.PARTIALLY_PAID,
    name: orderStatusMapping[orderStatusConstants.PARTIALLY_PAID],
  },
  {
    value: orderStatusConstants.NO_BALANCE_DUE,
    name: orderStatusMapping[orderStatusConstants.NO_BALANCE_DUE],
  },
];

const ORDER_TYPE_OPTIONS = [
  {
    value: orderTypeConstants.BUNDLED,
    name: orderTypeShortMapping[orderTypeConstants.BUNDLED],
  },
  {
    value: orderTypeConstants.ED,
    name: orderTypeShortMapping[orderTypeConstants.ED],
  },
  {
    value: orderTypeConstants.PATIENT_RESPONSIBILITY,
    name: orderTypeShortMapping[orderTypeConstants.PATIENT_RESPONSIBILITY],
  },
  {
    value: orderTypeConstants.BARIATRICS,
    name: orderTypeShortMapping[orderTypeConstants.BARIATRICS],
  },
  {
    value: orderTypeConstants.GFE,
    name: orderTypeShortMapping[orderTypeConstants.GFE],
  },
];

const transformSearchParam = (value: string | null, isDate = false) => {
  if (!value || value === "") return [];
  if (isDate) {
    return value
      .trim()
      .split(",")
      .map((item) => new Date(item.trim()));
  }
  return value
    .trim()
    .split(",")
    .map((item) => item.trim());
};

export function OrdersTable({
  accountId,
  directPayEnabled,
  hstImportEnabled,
  athenaImportEnabled,
}: {
  accountId?: string;
  directPayEnabled?: boolean;
  hstImportEnabled?: boolean;
  athenaImportEnabled?: boolean;
}) {
  const { getClearPriceByOrderType } = usePrice();
  const { loginWithRedirect } = useAuth0();
  const query = useQuery();

  const [login] = useLoginWithOrderIdAndDOBMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { pageViewEvents } = useAnalytics();
  const { current: toastElement } = useContext(ToastContext);

  const [rows] = useState(25);
  // Initialize first based on page query param (page is 1-based, first is 0-based)
  const pageFromUrl = Number(query.get("page")) || 1;
  const [first, setFirst] = useState((pageFromUrl - 1) * rows);
  const [filters, setFilters] = useState({
    status: transformSearchParam(query.get("status")) as string[],
    orderType: transformSearchParam(query.get("orderType")) as string[],
    patientName: query.get("patientName") || "",
    dateOfService: transformSearchParam(query.get("dateOfService"), true) as
      | Date[]
      | [],
    sortOrder: Number(query.get("sortOrder")) as DataTableSortOrderType,
    sortField: query.get("sortField") || (undefined as string | undefined),
    changedByEligibilityRerun:
      query.get("changedByEligibilityRerun") === "true",
  });
  const [dates, setDates] = useState<Date[]>(
    transformSearchParam(query.get("dateOfService"), true) as Date[] | []
  );
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [showCopyOrderDialog, setShowCopyOrderDialog] = useState(false);
  const [orderToCopy, setOrderToCopy] = useState<string>("");
  const [showHSTImportDialog, setShowHSTImportDialog] = useState(false);
  const [showAthenaImportDialog, setShowAthenaImportDialog] = useState(false);

  useEffect(() => {
    const handler = debounce(() => setDebouncedFilters(filters), 1000);
    handler();
    return () => {
      handler.cancel();
    };
  }, [filters]);

  const {
    data: orders,
    isLoading,
    isFetching,
    isError,
  } = useGetOrdersQuery({
    accountId,
    page: first,
    pageSize: rows,
    ...debouncedFilters,
  });

  useEffect(() => {
    if (isError) loginWithRedirect();
  }, [isError]);

  const parsedOrders = useMemo(() => {
    if (!orders) return [];

    const { data: ordersToParse } = orders;

    const parsed = ordersToParse.map((order) => {
      const { clearPrice } = getClearPriceByOrderType({
        order,
      });
      const { payments } = order;
      const sortedPayments =
        [...payments].sort((a, b) => {
          const dateA = new Date(a.createdAt || new Date(0)).getTime();
          const dateB = new Date(b.createdAt || new Date(0)).getTime();
          return dateA - dateB;
        }) || [];
      const lastPayment = sortedPayments[sortedPayments.length - 1];
      const latestRerunPrices = getLatestRerunPrices(order.orderHistories);
      return {
        orderId: order.id,
        status: order.status,
        orderType: order.orderType,
        orderAmount: clearPrice,
        latestRerunOldClearPrice: latestRerunPrices.old,
        latestRerunNewClearPrice: latestRerunPrices.new,
        dateOfService: parseDate(order.dateOfService),
        patientName: `${order.patient.firstName} ${order.patient.lastName}`,
        payerName: (order.serviceDetails as any)?.payerName || "",
        externalId: order.externalId,
        patient: order.patient,
        account: order.account,
        createdAt: parseDate(order.createdAt, false, true),
        lastPaymentDate: lastPayment?.createdAt || null,
      };
    });
    return parsed;
  }, [orders]);

  // CLE-2677 — memoised: rebuilding the debounced function every render gave each keystroke its own
  // timer, so nothing was ever coalesced and every character rewrote the URL.
  const debouncedFilterChange = useMemo(
    () => debounce((updateFilter: () => void) => updateFilter(), 500),
    []
  );

  useEffect(
    () => () => debouncedFilterChange.cancel(),
    [debouncedFilterChange]
  );

  function handleOnChangeFilter({
    newValue,
    field,
  }: {
    newValue: CalendarValueType | string;
    field: "patientName" | "status" | "orderType" | "dateOfService";
  }) {
    setFilters((prevData) => ({ ...prevData, [field]: newValue }));
    // CLE-2677 — back to page 1 whenever a filter changes. Every one of these narrows the result
    // set, so staying on page 3 while searching showed "No orders found." for a search that matched.
    setFirst(0);
    const updateFilter = () => {
      let valueToSet = "";
      if (newValue instanceof Date) {
        valueToSet = newValue.toISOString();
      } else if (Array.isArray(newValue)) {
        if (newValue.every((item) => item instanceof Date)) {
          valueToSet = newValue.map((date) => date.toISOString()).join(",");
        } else if (newValue.every((item) => typeof item === "string")) {
          valueToSet = newValue.join(",");
        } else if (
          newValue.every((item) => item === null || item instanceof Date)
        ) {
          valueToSet = newValue
            .filter((item) => item instanceof Date) // Only convert Date objects
            .map((date) => date.toISOString())
            .join(",");
        }
      } else if (typeof newValue === "string") {
        valueToSet = newValue;
      } else {
        valueToSet = "";
      }
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(field, valueToSet);
      searchParams.delete("page");
      navigate(`?${searchParams.toString()}`, { replace: true });
    };
    debouncedFilterChange(updateFilter);
  }

  // CLE-2520 — the "changed balance due to autorun" toggle. Applied immediately (a single click, not
  // typing) and resets to page 1, since the filter usually shrinks the result set enough that the
  // current page would otherwise be empty.
  //
  function handleOnChangeRerunFilter(checked: boolean) {
    setFilters((prevData) => ({
      ...prevData,
      changedByEligibilityRerun: checked,
    }));
    setFirst(0);
    const searchParams = new URLSearchParams(window.location.search);
    if (checked) {
      searchParams.set("changedByEligibilityRerun", "true");
    } else {
      searchParams.delete("changedByEligibilityRerun");
    }
    searchParams.delete("page");
    navigate(`?${searchParams.toString()}`, { replace: true });
  }

  const onClick = (row: any) => {
    const currentQueryParams = new URLSearchParams(location.search);
    // Remove page parameter when navigating to order view
    currentQueryParams.delete("page");
    if (row.status === orderStatusConstants.DRAFT && accountId) {
      currentQueryParams.set("accountId", accountId);
      const queryString = `?${currentQueryParams.toString()}`;
      // NAVIGATE TO THE REVIEW PAGE WITH THE DRAFT ORDER DATA
      navigate(`/orders/review/${row.orderId}${queryString}`);
      return;
    }
    const queryString = `?${currentQueryParams.toString()}`;
    navigate(`/orders/${row.orderId}${queryString}`);
  };

  const onCheckoutClick = (row: any) => {
    login({
      orderId: row.externalId || "",
      dateOfBirth: new Date(row.patient.dateOfBirth).toISOString(),
    })
      .unwrap()
      .then(({ token }: { token: string }) => {
        dispatch(onPublicTokenChange({ token, orderId: row.orderId }));
        dispatch(
          onOrderOverviewStepChange({
            step: Steps.ORDER_OVERVIEW,
          })
        );
        pageViewEvents(
          {
            accountName: row.account.name,
            orderType: row.orderType,
            communicationMethod: row.patient.preferredContactMethod,
          },
          events.CLICK_ACCEPT_PAYMENT
        );
        navigate(`/order/${row.externalId}?internalUser=true`);
      })
      .catch(() => {
        const detail =
          "Authentication failed. Please check your date of birth.";
        toastElement?.show({
          detail,
          summary: "Error",
          severity: "error",
        });
      });
  };

  const onPageChange = (event: PaginatorPageState) => {
    setFirst(event.first);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("page", (event.page + 1).toString());
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  function orderAction(row: any) {
    return (
      <Button
        className="p-button-link px-0"
        data-testid="order_action_OrderTable"
        onClick={() => onClick(row)}
      >
        <p className="small-text">{`${
          row.status === orderStatusConstants.DRAFT ? "Review" : "View"
        }`}</p>
      </Button>
    );
  }

  const onSort = (e: any) => {
    const { sortField } = e;
    let newSortOrder = 1 as DataTableSortOrderType;
    let newSortField = sortField;
    if (filters.sortOrder === 1) {
      newSortOrder = -1;
    } else if (filters.sortOrder === -1) {
      newSortOrder = 0;
      newSortField = undefined;
    }

    setFilters((prevData) => ({
      ...prevData,
      sortField: newSortField || undefined,
      sortOrder: newSortOrder,
    }));
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("sortField", newSortField);
    searchParams.set("sortOrder", newSortOrder?.toString() || "");
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  const currentPageRowCount = parsedOrders.length;

  function sortableColumnHeader(
    title: string,
    isSorted: boolean,
    field: string
  ) {
    let iconClass = "pi pi-sort-alt";
    if (isSorted) {
      iconClass =
        filters.sortOrder === 1
          ? "pi pi-sort-amount-up"
          : "pi pi-sort-amount-down";
    }
    return (
      <>
        {`${title} (${currentPageRowCount})`}
        <i
          className={iconClass}
          tabIndex={0}
          role="button"
          aria-label="Sort by field name"
          title="Sort by field name"
          onClick={() => onSort({ sortField: field })}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              onSort({ sortField: field });
            }
          }}
        />
      </>
    );
  }

  function orderCheckoutAction(row: any) {
    if (row.status !== orderStatusConstants.SENT_TO_PATIENT) {
      return null;
    }
    return (
      <Button
        className="p-button-link p-0"
        data-testid="order_action_OrderTable"
        onClick={() => onCheckoutClick(row)}
      >
        <p className="small-text">Checkout</p>
      </Button>
    );
  }
  function orderCopyAction(row: any) {
    return (
      <Button
        icon="pi pi-copy"
        className="p-button-rounded p-button-text"
        data-testid="order_action_OrderTable"
        onClick={() => {
          setOrderToCopy(row.orderId);
          setShowCopyOrderDialog(true);
        }}
        tooltip="Copy Order"
        tooltipOptions={{ position: "top" }}
      />
    );
  }

  function getPatientName(row: any) {
    return (
      <div className="sensitive" data-pii="patient-name">
        {row.patientName}
      </div>
    );
  }

  // CLE-2677 — only the FIRST load swaps the whole table for a spinner. Refetches (every filter
  // change, including each debounced keystroke in the search box) keep the filter bar mounted and
  // show the DataTable's own loading overlay instead: unmounting the input mid-typing threw away
  // the caret, so a long account number could never be typed out.
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <CopyOrderDialog
        showCopyOrderDialog={showCopyOrderDialog}
        setShowCopyOrderDialog={setShowCopyOrderDialog}
        setOrderToCopy={setOrderToCopy}
        orderId={orderToCopy}
        accountId={accountId}
      />
      {accountId && (
        <HSTImportDialog
          visible={showHSTImportDialog}
          onHide={() => setShowHSTImportDialog(false)}
          accountId={accountId}
        />
      )}
      <AthenaImportDialog
        visible={showAthenaImportDialog}
        onHide={() => setShowAthenaImportDialog(false)}
      />
      <ItemBar>
        <ItemBarLeft>
          {/* CLE-2677 — one box, five things: patient name (any word order), account number
              (partial), or — on the PRIMARY insurance only — the MRN / member ID, the payer, or the
              estimate ID. The placeholder names only what fits; the title spells the rest out on
              hover. The query param keeps its old `patientName` name so existing links and bookmarks
              still work; the server decides what the string matches. */}
          <StyledSearchBar className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              data-testid="name_multiselect_OrderTable"
              id="patientSearch"
              value={filters.patientName}
              placeholder="Search patient, MRN, payer, or estimate ID..."
              title="Search by patient name (any order), account number, estimate ID, or — on the primary insurance only — MRN / member ID or payer"
              onChange={(e) =>
                handleOnChangeFilter({
                  newValue: e.target.value,
                  field: "patientName",
                })
              }
            />
          </StyledSearchBar>
          <MultiSelect
            data-testid="type_multiselect_OrderTable"
            display="chip"
            optionLabel="name"
            placeholder="Order type"
            value={filters.orderType}
            options={ORDER_TYPE_OPTIONS}
            onChange={(e) =>
              handleOnChangeFilter({
                newValue: e.target.value,
                field: "orderType",
              })
            }
          />
          <Calendar
            data-testid="range_calendar_OrderTable"
            selectionMode="range"
            placeholder="Date of Service"
            value={dates}
            onChange={(e) => {
              const newDates = e.value as Date[];
              setDates(newDates);
              if (!newDates || newDates.length === 0) {
                handleOnChangeFilter({
                  field: "dateOfService",
                  newValue: [],
                });
              }
            }}
            onHide={() => {
              const firstDate = dates?.[0];
              const secondDate = dates?.[1];
              if (firstDate && secondDate) {
                handleOnChangeFilter({
                  field: "dateOfService",
                  newValue: [firstDate, secondDate],
                });
              } else if (firstDate) {
                handleOnChangeFilter({
                  field: "dateOfService",
                  newValue: [firstDate, firstDate],
                });
              } else {
                handleOnChangeFilter({
                  field: "dateOfService",
                  newValue: [],
                });
              }
            }}
          />
          <MultiSelect
            data-testid="status_multiselect_OrderTable"
            display="chip"
            optionLabel="name"
            placeholder="Status"
            value={filters.status}
            options={STATUS_OPTIONS}
            onChange={(e) =>
              handleOnChangeFilter({
                newValue: e.target.value,
                field: "status",
              })
            }
          />
        </ItemBarLeft>
        <ItemBarRight>
          {directPayEnabled && (
            <StyledButton
              data-testid="direct_pay_order_OrderTable"
              icon="pi pi-credit-card"
              className="p-button-raised"
              onClick={() =>
                navigate(`/order/direct-pay?accountId=${accountId}`)
              }
            >
              Direct Pay
            </StyledButton>
          )}
          {hstImportEnabled && (
            <StyledButton
              data-testid="hst_import_OrderTable"
              icon="pi pi-upload"
              className="p-button-raised"
              onClick={() => setShowHSTImportDialog(true)}
            >
              Import from EHR
            </StyledButton>
          )}
          {athenaImportEnabled && (
            <StyledButton
              data-testid="athena_import_OrderTable"
              icon="pi pi-upload"
              className="p-button-raised"
              onClick={() => setShowAthenaImportDialog(true)}
            >
              Import from Athena
            </StyledButton>
          )}
          <StyledButton
            data-testid="create_new_order_OrderTable"
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate(`/orders/new?accountId=${accountId}`)}
          >
            Create new order
          </StyledButton>
        </ItemBarRight>
      </ItemBar>

      <StyledRerunFilter>
        <Checkbox
          inputId="changed_by_eligibility_rerun_OrderTable"
          data-testid="changed_by_eligibility_rerun_OrderTable"
          checked={filters.changedByEligibilityRerun}
          onChange={(e) => handleOnChangeRerunFilter(Boolean(e.checked))}
        />
        <label htmlFor="changed_by_eligibility_rerun_OrderTable">
          Show all orders that have changed balance due to autorun
        </label>
      </StyledRerunFilter>

      <StyledPageDataTable
        emptyMessage="No orders found."
        stripedRows
        className="p-fluid"
        value={parsedOrders}
        loading={isFetching}
        onRowClick={({ data, originalEvent }) =>
          originalEvent.detail > 1 && onClick(data)
        }
        removableSort
        sortMode="single"
        onSort={onSort}
        responsiveLayout="scroll"
      >
        <Column
          field="patientName"
          header={`Patient Name (${currentPageRowCount})`}
          headerStyle={{ width: "15%" }}
          body={(row) => getPatientName(row)}
        />
        <Column
          field="orderType"
          header={`Order Type (${currentPageRowCount})`}
          body={(row) => orderTypeShortMapping[row.orderType]}
          headerStyle={{ width: "13%" }}
        />
        {orders?.hasGfe && (
          <Column
            field="payerName"
            header={`Payer (${currentPageRowCount})`}
            headerStyle={{ width: "12%" }}
          />
        )}
        <Column
          field="createdAt"
          header={`Date Created (${currentPageRowCount})`}
          headerStyle={{ width: "10%" }}
        />
        <Column
          field="dateOfService"
          className={filters.sortField === "dateOfService" ? "p-highlight" : ""}
          header={sortableColumnHeader(
            "Date of Service",
            filters.sortField === "dateOfService",
            "dateOfService"
          )}
          body={(row) =>
            row.dateOfService === NOT_SCHEDULED_DATE
              ? "Not scheduled"
              : row.dateOfService
          }
          sortable
          headerStyle={{ width: "10%" }}
        />
        <Column
          field="status"
          header={`Status (${currentPageRowCount})`}
          body={(row) => orderStatusMapping[row.status]}
          headerStyle={{ width: "10%" }}
        />
        <Column
          field="lastPaymentDate"
          className={
            filters.sortField === "lastPaymentDate" ? "p-highlight" : ""
          }
          header={sortableColumnHeader(
            "Paid/Refunded Date",
            filters.sortField === "lastPaymentDate",
            "lastPaymentDate"
          )}
          body={(row) =>
            row.lastPaymentDate
              ? parseDate(row.lastPaymentDate, false, true)
              : " "
          }
          sortable
          headerStyle={{ width: "12%" }}
        />
        <Column
          field="orderAmount"
          header={`Clear Price for patient (${currentPageRowCount})`}
          body={(row) => parsePrice(centsToDollars(row.orderAmount))}
          headerStyle={{ width: "12%" }}
        />
        <Column
          field="latestRerunOldClearPrice"
          header="Latest eligibility rerun (new/old)"
          body={latestRerunPriceBody}
          headerStyle={{ width: "12%" }}
        />
        <Column
          header=""
          field="checkout"
          body={(row) => orderCheckoutAction(row)}
          headerStyle={{ width: "5%" }}
        />
        <Column
          header=""
          field="viewOrder"
          body={(row) => orderAction(row)}
          headerStyle={{ width: "12%" }}
        />
        <Column
          header=""
          field="copy"
          body={(row) => orderCopyAction(row)}
          headerStyle={{ width: "11%" }}
        />
      </StyledPageDataTable>
      <Paginator
        first={first}
        rows={rows}
        totalRecords={orders?.total}
        onPageChange={onPageChange}
      />
    </div>
  );
}
